import G6, { type Graph as G6Graph, type INode, type IEdge } from '@antv/g6'
import { nextTick } from 'vue'
import type { GraphNode, GraphEdge } from '../@types'

export interface GraphInitOptions {
  container: HTMLElement
  width?: number
  height?: number
}

export interface EditCallbacks {
  onAddNode?: (x: number, y: number) => void
  /** @deprecated 使用 onProposeConnection */
  onAddEdge?: (sourceId: string, targetId: string) => void
  /** 用户完成一次连线操作（两节点已选定），由 UI 层弹窗确认后再调用 addEdge */
  onProposeConnection?: (sourceId: string, targetId: string) => void
  onDeleteNode?: (nodeId: string) => void
  onDeleteEdge?: (edgeId: string) => void
  onDataChange?: (nodes: GraphNode[], edges: GraphEdge[]) => void
  /** 右键节点菜单回调 */
  onNodeContextMenu?: (nodeId: string, label: string, color: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
  /** 右键边菜单回调 */
  onEdgeContextMenu?: (edgeId: string, sourceId: string, targetId: string, label: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
}

export interface InitOptions {
  editMode?: boolean
  onAddNode?: (x: number, y: number) => void
  onAddEdge?: (sourceId: string, targetId: string) => void
  onProposeConnection?: (sourceId: string, targetId: string) => void
  onDeleteNode?: (nodeId: string) => void
  onDeleteEdge?: (edgeId: string) => void
  onDataChange?: (nodes: GraphNode[], edges: GraphEdge[]) => void
  onNodeContextMenu?: (nodeId: string, label: string, color: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
  onEdgeContextMenu?: (edgeId: string, sourceId: string, targetId: string, label: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
}

/**
 * G6 图谱基础类：负责 Graph 生命周期、尺寸、fitView 等通用能力
 */
export class BaseG6Graph {
  protected graph: G6Graph | null = null
  protected container: HTMLElement | null = null

  private resizeHandler: (() => void) | null = null

  protected createGraph(config: Omit<ConstructorParameters<typeof G6Graph>[0], 'container' | 'width' | 'height'>, options: GraphInitOptions) {
    const { container, width, height } = options

    this.container = container
    const w = width || container.offsetWidth
    const h = height || container.offsetHeight

    if (this.graph) {
      this.destroy()
    }

    this.graph = new G6.Graph({
      container,
      width: w,
      height: h,
      ...config
    })

    this.setupResizeHandler()
  }

  private setupResizeHandler() {
    this.cleanupResizeHandler()

    this.resizeHandler = () => {
      if (!this.graph || !this.container) return
      const newWidth = this.container.offsetWidth
      const newHeight = this.container.offsetHeight
      this.graph.changeSize(newWidth, newHeight)
      requestAnimationFrame(() => {
        this.fitView()
      })
    }

    window.addEventListener('resize', this.resizeHandler)
  }

  private cleanupResizeHandler() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler = null
    }
  }

  public hasGraph(): boolean {
    return !!this.graph
  }

  public fitView(padding: number = 20) {
    if (this.graph) {
      try {
        this.graph.fitView(padding)
      } catch {
        // ignore
      }
    }
  }

  public destroy() {
    this.cleanupResizeHandler()
    if (this.graph) {
      try {
        this.graph.destroy()
      } catch {
        // destroy 需要幂等：G6 内部若已销毁/状态异常，忽略错误以保证上层可安全切换渲染引擎
      }
      this.graph = null
    }
    this.container = null
  }
}

/**
 * 具体的知识图谱实现：白底黑字 + 实体/关系配色 + 力导布局
 */
/** 布局类型 */
export type LayoutType = 'force' | 'circular'
export type GraphTheme = 'light' | 'dark'

/** 编辑工具类型 */
export type ToolType = 'select' | 'addNode' | 'addEdge' | 'deleteNode' | 'deleteEdge'

/** 工具交互回调 */
export interface EditToolCallbacks {
  onNodeClick?: (nodeId: string) => void
  onCanvasClick?: (x: number, y: number) => void
}

export class KnowledgeGraph extends BaseG6Graph {
  // 右键相关事件句柄，方便销毁时清理
  private contextMenuHandler: ((e: MouseEvent) => void) | null = null
  private globalMouseUpHandler: ((e: MouseEvent) => void) | null = null
  private globalKeyDownHandler: ((e: KeyboardEvent) => void) | null = null

  /** 当前布局类型，用于切换布局 */
  private currentLayoutType: LayoutType = 'force'
  /** 图谱主题 */
  private theme: GraphTheme = 'light'

  /** ========== 编辑模式相关 ========== */
  private isEditMode: boolean = false
  private editCallbacks: EditCallbacks = {}
  private pendingEdge: { sourceId: string; x: number; y: number } | null = null
  private editEventsBound: boolean = false
  private linkPreviewSvg: SVGSVGElement | null = null
  private linkPreviewLine: SVGLineElement | null = null
  private pendingPreviewMove: ((e: MouseEvent) => void) | null = null
  private altLinkDrag: { sourceId: string } | null = null
  private altLinkMove: ((e: MouseEvent) => void) | null = null
  private altLinkUp: ((e: MouseEvent) => void) | null = null
  /** Alt 拖线松手后 G6 仍会触发 node:click，用此标志吞掉一次点击 */
  private suppressNextNodeClick = false

  /** ========== 工具模式相关 ========== */
  private toolType: ToolType = 'select'
  private editToolCallbacks: EditToolCallbacks = {}
  /** addEdge 模式：已选中的源节点 */
  private edgeSourceNodeId: string | null = null
  /** addEdge 模式：预览连线源节点坐标 */
  private edgeSourcePos: { x: number; y: number } | null = null

  /** 设置编辑回调函数 */
  public setEditCallbacks(callbacks: EditCallbacks) {
    this.editCallbacks = callbacks
  }

  /** 设置工具交互回调（用于工具模式下的节点/画布点击通知） */
  public setEditToolCallbacks(callbacks: EditToolCallbacks) {
    this.editToolCallbacks = callbacks
  }

  public setTheme(theme: GraphTheme) {
    this.theme = theme
    this.applyThemeToExistingGraph()
  }

  private getThemePalette() {
    if (this.theme === 'dark') {
      return {
        nodeStroke: '#94a3b8',
        nodeLabel: '#e2e8f0',
        edgeDefault: '#4b5563',
        edgeLabel: '#94a3b8',
        hoverStroke: '#60a5fa',
        selectedStroke: '#f43f5e',
        editSelectedStroke: '#60a5fa',
        editPendingStroke: '#f59e0b',
        edgeEditSelectedStroke: '#34d399'
      }
    }
    return {
      nodeStroke: '#333333',
      nodeLabel: '#000000',
      edgeDefault: '#cccccc',
      edgeLabel: '#666666',
      hoverStroke: '#40a9ff',
      selectedStroke: '#ff4d4f',
      editSelectedStroke: '#3b82f6',
      editPendingStroke: '#f59e0b',
      edgeEditSelectedStroke: '#10b981'
    }
  }

  private applyThemeToExistingGraph() {
    if (!this.graph) return
    const palette = this.getThemePalette()
    this.graph.getNodes().forEach((node: INode) => {
      const model = node.getModel() as any
      this.graph!.updateItem(node, {
        style: {
          ...(model.style || {}),
          stroke: model.style?.stroke || palette.nodeStroke
        },
        labelCfg: {
          ...(model.labelCfg || {}),
          style: {
            ...(model.labelCfg?.style || {}),
            fill: palette.nodeLabel
          }
        }
      } as any)
    })
    this.graph.getEdges().forEach((edge: IEdge) => {
      const model = edge.getModel() as any
      const stroke = model.style?.stroke || this.getColorByRelation(model.relation_type)
      this.graph!.updateItem(edge, {
        style: {
          ...(model.style || {}),
          stroke,
          endArrow: {
            path: G6.Arrow.triangle(10, 10, 0),
            fill: stroke
          }
        },
        labelCfg: {
          ...(model.labelCfg || {}),
          style: {
            ...(model.labelCfg?.style || {}),
            fill: stroke || palette.edgeLabel
          }
        }
      } as any)
    })
    this.graph.paint()
  }

  /** 设置当前工具类型，同时更新 canvas cursor */
  public setToolType(type: ToolType) {
    this.toolType = type
    if (!this.container) return
    // 工具切换时重置 addEdge 选择状态
    if (type !== 'addEdge') {
      this.resetEdgeSelectState()
    }
    // 更新 cursor
    const cursorMap: Record<ToolType, string> = {
      select: 'default',
      addNode: 'crosshair',
      addEdge: 'crosshair',
      deleteNode: 'not-allowed',
      deleteEdge: 'not-allowed'
    }
    this.container.style.cursor = this.isEditMode ? (cursorMap[type] || 'crosshair') : 'default'
  }

  /** 获取当前工具类型 */
  public getToolType(): ToolType {
    return this.toolType
  }

  private resetEdgeSelectState() {
    if (!this.graph) return
    if (this.edgeSourceNodeId) {
      const n = this.graph.findById(this.edgeSourceNodeId)
      if (n) this.graph.clearItemStates(n, ['edit-selected'])
    }
    this.edgeSourceNodeId = null
    this.edgeSourcePos = null
    this.hideLinkPreview()
    this.detachPendingPreviewListeners()
  }

  /** 工具模式节点点击处理（由 GraphVisualization 调用） */
  public handleToolNodeClick(nodeId: string, x: number, y: number) {
    this.editToolCallbacks.onNodeClick?.(nodeId)
  }

  /** 工具模式画布点击处理（由 GraphVisualization 调用） */
  public handleToolCanvasClick(x: number, y: number) {
    this.editToolCallbacks.onCanvasClick?.(x, y)
  }

  /** 设置编辑模式 */
  public setEditMode(enabled: boolean) {
    this.isEditMode = enabled
    if (!this.graph) return

    if (enabled) {
      const cursorMap: Record<ToolType, string> = {
        select: 'default',
        addNode: 'crosshair',
        addEdge: 'crosshair',
        deleteNode: 'not-allowed',
        deleteEdge: 'not-allowed'
      }
      if (this.container) {
        this.container.style.cursor = cursorMap[this.toolType] || 'crosshair'
      }
      if (!this.editEventsBound) {
        this.setupEditModeEvents()
        this.editEventsBound = true
      }
    } else {
      if (this.container) {
        this.container.style.cursor = 'default'
      }
      this.clearPendingConnection()
      this.resetEdgeSelectState()
      if (this.graph) {
        this.graph.getNodes().forEach(node => {
          this.graph!.clearItemStates(node, ['edit-selected', 'edit-pending'])
        })
        this.graph.getEdges().forEach(edge => {
          this.graph!.clearItemStates(edge, ['edit-selected'])
        })
      }
    }
    console.log('[KnowledgeGraph] setEditMode:', enabled)
  }

  /** 获取当前数据（用于保存） */
  public getEditData(): { nodes: GraphNode[], edges: GraphEdge[] } {
    if (!this.graph) return { nodes: [], edges: [] }

    const nodes = this.graph.getNodes().map((node: INode) => {
      const model = node.getModel() as any
      return {
        id: model.id,
        label: model.label,
        type: model.type,
        size: model.size,
        style: model.style,
        x: model.x,
        y: model.y
      }
    })

    const edges = this.graph.getEdges().map((edge: IEdge) => {
      const model = edge.getModel() as any
      return {
        id: model.id,
        source: model.source,
        target: model.target,
        label: model.label,
        relation_type: model.relation_type,
        style: model.style
      }
    })

    return { nodes, edges }
  }

  /**
   * 更新指定节点的标签
   */
  public updateNodeLabel(nodeId: string, newLabel: string) {
    if (!this.graph) return
    const node = this.graph.findById(nodeId)
    if (node) {
      this.graph.updateItem(node, { label: newLabel })
    }
  }

  /**
   * 更新指定节点的颜色（fill + stroke）
   */
  public updateNodeColor(nodeId: string, color: string) {
    if (!this.graph) return
    const node = this.graph.findById(nodeId)
    if (node) {
      this.graph.updateItem(node, {
        style: {
          fill: color,
          stroke: color,
          lineWidth: 2
        }
      })
    }
  }

  /**
   * 更新指定边的属性（label、source、target）
   */
  public updateEdge(edgeId: string, updates: { label?: string; source?: string; target?: string }) {
    if (!this.graph) return
    const edge = this.graph.findById(edgeId)
    if (edge) {
      this.graph.updateItem(edge, updates)
    }
  }

  /**
   * 删除节点
   */
  public deleteNode(nodeId: string) {
    if (!this.graph) return
    const node = this.graph.findById(nodeId)
    if (node) {
      this.graph.removeItem(node)
    }
  }

  /**
   * 删除边
   */
  public deleteEdge(edgeId: string) {
    if (!this.graph) return
    const edge = this.graph.findById(edgeId)
    if (edge) {
      this.graph.removeItem(edge)
    }
  }

  /**
   * 在画布上新增节点（编辑模式）
   */
  public addNodeAt(x: number, y: number, label: string, type: string = 'entity') {
    if (!this.graph) return
    const id = `node_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const fill = this.getColorByType(type)
    this.graph.addItem('node', {
      id,
      label,
      type,
      x,
      y,
      size: 32,
      style: {
        fill,
        stroke: '#ffffff',
        lineWidth: 2
      },
      labelCfg: {
        position: 'bottom',
        style: {
          fill: this.getThemePalette().nodeLabel,
          fontSize: 16,
          fontWeight: 'bold'
        }
      }
    } as any)
  }

  /**
   * 新增一条有向边（编辑模式）
   */
  public addEdgeBetween(sourceId: string, targetId: string, label?: string) {
    if (!this.graph) return
    if (sourceId === targetId) return
    const exists = this.graph.getEdges().some((ed: IEdge) => {
      const m = ed.getModel() as any
      return m.source === sourceId && m.target === targetId
    })
    if (exists) return
    const relationLabel = label?.trim() || '相关'
    const relation_type = relationLabel
    const relationColor = this.getColorByRelation(relation_type)
    const id = `edge_${sourceId}_${targetId}_${Date.now()}`
    this.graph.addItem('edge', {
      id,
      source: sourceId,
      target: targetId,
      label: relationLabel,
      relation_type,
      style: {
        stroke: relationColor,
        lineWidth: 2,
        endArrow: {
          path: G6.Arrow.triangle(10, 10, 0),
          fill: relationColor
        }
      },
      labelCfg: {
        autoRotate: true,
        style: {
          fill: relationColor,
          fontSize: 12
        }
      }
    } as any)
  }

  /** 从指定节点开始「拉出连线」状态（等价于第一次点选源节点） */
  public startConnectFrom(nodeId: string) {
    if (!this.graph || !this.isEditMode) return
    const node = this.graph.findById(nodeId) as INode | undefined
    if (!node) return
    this.endAltLinkDrag()
    if (this.pendingEdge) {
      const prev = this.graph.findById(this.pendingEdge.sourceId)
      if (prev) this.graph.clearItemStates(prev, ['edit-pending'])
    }
    this.graph.setItemState(node, 'edit-pending', true)
    const model = node.getModel() as any
    this.pendingEdge = { sourceId: nodeId, x: model.x ?? 0, y: model.y ?? 0 }
    this.attachPendingPreviewListeners()
  }

  /** 画布视口中心对应的图坐标（用于在中心添加节点） */
  public getViewportCenterGraphPoint(): { x: number; y: number } | null {
    if (!this.graph || !this.container) return null
    const rect = this.container.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    return this.graph.getPointByClient(cx, cy)
  }

  public getNodeDisplayLabel(nodeId: string): string {
    if (!this.graph) return nodeId
    const node = this.graph.findById(nodeId) as INode | undefined
    if (!node) return nodeId
    const model = node.getModel() as any
    return String(model.label ?? model.id ?? nodeId)
  }

  private fireProposeConnection(sourceId: string, targetId: string) {
    const cb = this.editCallbacks.onProposeConnection ?? this.editCallbacks.onAddEdge
    cb?.(sourceId, targetId)
  }

  private ensureLinkPreviewLayer() {
    if (!this.container || this.linkPreviewSvg) return
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('class', 'kg-link-preview-svg')
    svg.style.cssText =
      'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:60;overflow:visible'
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker')
    marker.setAttribute('id', 'kg-link-arrow')
    marker.setAttribute('markerWidth', '10')
    marker.setAttribute('markerHeight', '10')
    marker.setAttribute('refX', '10')
    marker.setAttribute('refY', '3')
    marker.setAttribute('orient', 'auto')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'M0,0 L0,6 L9,3 z')
    path.setAttribute('fill', this.getThemePalette().editSelectedStroke)
    marker.appendChild(path)
    defs.appendChild(marker)
    svg.appendChild(defs)
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.setAttribute('stroke', this.getThemePalette().editSelectedStroke)
    line.setAttribute('stroke-width', '2')
    line.setAttribute('stroke-dasharray', '6 4')
    line.setAttribute('marker-end', 'url(#kg-link-arrow)')
    line.setAttribute('x1', '0')
    line.setAttribute('y1', '0')
    line.setAttribute('x2', '0')
    line.setAttribute('y2', '0')
    line.style.display = 'none'
    svg.appendChild(line)
    this.container.style.position = this.container.style.position || 'relative'
    this.container.appendChild(svg)
    this.linkPreviewSvg = svg
    this.linkPreviewLine = line
  }

  private hideLinkPreview() {
    if (this.linkPreviewLine) {
      this.linkPreviewLine.style.display = 'none'
    }
  }

  private showLinkPreviewLine(x1: number, y1: number, x2: number, y2: number) {
    this.ensureLinkPreviewLayer()
    if (!this.linkPreviewLine) return
    this.linkPreviewLine.setAttribute('x1', String(x1))
    this.linkPreviewLine.setAttribute('y1', String(y1))
    this.linkPreviewLine.setAttribute('x2', String(x2))
    this.linkPreviewLine.setAttribute('y2', String(y2))
    this.linkPreviewLine.style.display = ''
  }

  /**
   * 将图数据坐标 (model.x / model.y) 转为浏览器视口 client 坐标。
   * 注意：keyShape.getBBox() 是节点局部坐标，不能当作图坐标使用。
   * refClientX/refClientY 在无 getClientByPoint 时用于局部线性反推（通常取当前鼠标位置）。
   */
  private graphPointToViewportClient(
    gx: number,
    gy: number,
    refClientX: number,
    refClientY: number
  ): { x: number; y: number } {
    const g: any = this.graph
    if (!g) return { x: refClientX, y: refClientY }

    // G6 4.x：getClientByPoint(x, y) 接收两个数字（视口/模型坐标），不是 { x, y } 对象
    if (typeof g.getClientByPoint === 'function') {
      try {
        const p = g.getClientByPoint(gx, gy)
        if (p && typeof p.x === 'number' && typeof p.y === 'number') {
          return { x: p.x, y: p.y }
        }
        if (Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number') {
          return { x: p[0], y: p[1] }
        }
      } catch {
        // fall through
      }
    }

    const callGPC = (cx: number, cy: number) => {
      if (typeof g.getPointByClient !== 'function') return null
      try {
        return g.getPointByClient(cx, cy) as { x: number; y: number } | null
      } catch {
        return null
      }
    }

    const eps = 40
    const p0 = callGPC(refClientX, refClientY)
    const px = callGPC(refClientX + eps, refClientY)
    const py = callGPC(refClientX, refClientY + eps)
    if (!p0 || !px || !py || typeof p0.x !== 'number' || typeof p0.y !== 'number') {
      return { x: refClientX, y: refClientY }
    }

    const j11 = (px.x - p0.x) / eps
    const j21 = (px.y - p0.y) / eps
    const j12 = (py.x - p0.x) / eps
    const j22 = (py.y - p0.y) / eps
    const dgx = gx - p0.x
    const dgy = gy - p0.y
    const det = j11 * j22 - j12 * j21
    if (Math.abs(det) < 1e-12) {
      return { x: refClientX, y: refClientY }
    }
    const dcx = (j22 * dgx - j12 * dgy) / det
    const dcy = (-j21 * dgx + j11 * dgy) / det
    return { x: refClientX + dcx, y: refClientY + dcy }
  }

  private updateLinkPreviewToClient(clientX: number, clientY: number) {
    if (!this.graph || !this.container) return
    let sourceId: string | null = null
    if (this.altLinkDrag) sourceId = this.altLinkDrag.sourceId
    else if (this.pendingEdge) sourceId = this.pendingEdge.sourceId
    else if (this.edgeSourceNodeId) sourceId = this.edgeSourceNodeId
    if (!sourceId) {
      this.hideLinkPreview()
      return
    }
    const sourceNode = this.graph.findById(sourceId) as INode | undefined
    if (!sourceNode) return

    const rect = this.container.getBoundingClientRect()
    const m = sourceNode.getModel() as any
    const gx = m.x ?? 0
    const gy = m.y ?? 0
    const clientP1 = this.graphPointToViewportClient(gx, gy, clientX, clientY)

    const x1 = clientP1.x - rect.left
    const y1 = clientP1.y - rect.top
    const x2 = clientX - rect.left
    const y2 = clientY - rect.top
    this.showLinkPreviewLine(x1, y1, x2, y2)
  }

  private findNodeIdAtClient(clientX: number, clientY: number): string | null {
    if (!this.graph || !this.container) return null
    const point = this.graph.getPointByClient(clientX, clientY)
    const nodes = this.graph.getNodes()
    for (const node of nodes) {
      const model = node.getModel() as any
      const dx = (model.x ?? 0) - point.x
      const dy = (model.y ?? 0) - point.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      const nodeRadius = (model.size ?? 20) + 12
      if (distance < nodeRadius) return model.id as string
    }
    return null
  }

  private attachPendingPreviewListeners() {
    this.detachPendingPreviewListeners()
    this.pendingPreviewMove = (e: MouseEvent) => this.updateLinkPreviewToClient(e.clientX, e.clientY)
    document.addEventListener('mousemove', this.pendingPreviewMove)
  }

  private detachPendingPreviewListeners() {
    if (this.pendingPreviewMove) {
      document.removeEventListener('mousemove', this.pendingPreviewMove)
      this.pendingPreviewMove = null
    }
  }

  private beginAltLinkDrag(sourceId: string) {
    if (!this.graph) return
    this.clearPendingEdgeOnly()
    this.endAltLinkDrag()
    this.altLinkDrag = { sourceId }
    const node = this.graph.findById(sourceId)
    if (node) this.graph.setItemState(node, 'edit-pending', true)
    this.altLinkMove = (e: MouseEvent) => this.updateLinkPreviewToClient(e.clientX, e.clientY)
    this.altLinkUp = (e: MouseEvent) => this.onAltLinkMouseUp(e)
    document.addEventListener('mousemove', this.altLinkMove)
    document.addEventListener('mouseup', this.altLinkUp)
  }

  private onAltLinkMouseUp(e: MouseEvent) {
    if (!this.altLinkDrag || !this.graph) {
      this.endAltLinkDrag()
      return
    }
    const sourceId = this.altLinkDrag.sourceId
    const targetId = this.findNodeIdAtClient(e.clientX, e.clientY)
    const sourceNode = this.graph.findById(sourceId)
    if (sourceNode) this.graph.clearItemStates(sourceNode, ['edit-pending'])
    this.endAltLinkDrag()
    this.hideLinkPreview()
    if (targetId && targetId !== sourceId) {
      this.suppressNextNodeClick = true
      this.fireProposeConnection(sourceId, targetId)
    }
  }

  private endAltLinkDrag() {
    if (this.altLinkMove) {
      document.removeEventListener('mousemove', this.altLinkMove)
      this.altLinkMove = null
    }
    if (this.altLinkUp) {
      document.removeEventListener('mouseup', this.altLinkUp)
      this.altLinkUp = null
    }
    this.altLinkDrag = null
    this.hideLinkPreview()
  }

  private clearPendingEdgeOnly() {
    if (!this.graph) return
    if (this.pendingEdge) {
      const n = this.graph.findById(this.pendingEdge.sourceId)
      if (n) this.graph.clearItemStates(n, ['edit-pending'])
    }
    this.pendingEdge = null
    this.detachPendingPreviewListeners()
    this.hideLinkPreview()
  }

  private clearPendingConnection() {
    this.clearPendingEdgeOnly()
    this.endAltLinkDrag()
  }

  /** 获取布局配置：力导向（更分散、少重叠）或环形（中心放射，适合枢纽图） */
  private getLayoutConfig(
    type: LayoutType,
    container: HTMLElement,
    width?: number,
    height?: number
  ) {
    const w = width || container.offsetWidth
    const h = height || container.offsetHeight
    const center: [number, number] = [w / 2, h / 2]

    if (type === 'circular') {
      return {
        type: 'circular',
        center,
        radius: Math.min(w, h) * 0.35,
        startAngle: Math.PI / 2,
        endAngle: Math.PI / 2 + Math.PI * 2,
        preventOverlap: true,
        nodeSize: (d: any) => (d.size || 30) + 50,
        nodeSpacing: 20
      }
    }

    // 力导向：加大间距与斥力，减弱向心力，减少重叠
    return {
      type: 'force',
      preventOverlap: true,
      nodeSize: (d: any) => Math.max(d.size || 30, 40) + 55,
      nodeSpacing: 28,
      linkDistance: 380,
      nodeStrength: -750,
      edgeStrength: 0.6,
      collideStrength: 1,
      gravity: 4,
      alpha: 0.4,
      alphaDecay: 0.018,
      alphaMin: 0.001,
      center
    }
  }

  // 根据实体类型返回颜色
  private getColorByType(entityType?: string): string {
    const colorMap: Record<string, string> = {
      人物: '#ff4d4f',
      地点: '#52c41a',
      组织: '#1890ff',
      概念: '#faad14',
      事件: '#722ed1',
      entity: '#13c2c2'
    }
    return colorMap[entityType || 'entity'] || '#13c2c2'
  }

  // 根据关系类型返回颜色（用来给关系线条和 label 上色）
  private getColorByRelation(relationType?: string): string {
    const relationColorMap: Record<string, string> = {
      因果: '#e74c3c',
      所属: '#2ecc71',
      引用: '#3498db',
      相关: '#9b59b6'
    }
    return relationColorMap[relationType || '相关'] || '#9b59b6'
  }

  public init(options: GraphInitOptions, nodes: GraphNode[], edges: GraphEdge[], editOptions?: InitOptions) {
    const { container, width, height } = options

    // 预处理数据
    const { processedNodes, processedEdges } = this.processData(nodes, edges)

    // 保存编辑回调
    if (editOptions) {
      this.isEditMode = editOptions.editMode ?? false
      this.editCallbacks = {
        onAddNode: editOptions.onAddNode,
        onAddEdge: editOptions.onAddEdge,
        onProposeConnection: editOptions.onProposeConnection,
        onDeleteNode: editOptions.onDeleteNode,
        onDeleteEdge: editOptions.onDeleteEdge,
        onDataChange: editOptions.onDataChange,
        onNodeContextMenu: editOptions.onNodeContextMenu,
        onEdgeContextMenu: editOptions.onEdgeContextMenu
      }
    }

    this.createGraph(
      {
        minZoom: 0.1,
        maxZoom: 5,
        modes: {
          default: [
            {
              type: 'zoom-canvas',
              sensitivity: 1,
              enableOptimize: true
            },
            'drag-node',
            'click-select'
          ]
        },
        layout: this.getLayoutConfig('force', container, width, height),
        defaultNode: {
          shape: 'circle',
          size: 28,
          style: {
            fill: '#ffffff',
            stroke: this.getThemePalette().nodeStroke,
            lineWidth: 2,
            cursor: 'pointer'
          },
          labelCfg: {
            position: 'bottom',
            style: {
              fill: this.getThemePalette().nodeLabel,
              fontSize: 16,
              fontWeight: 'bold'
            }
          }
        },
        defaultEdge: {
          shape: 'line',
          style: {
            stroke: this.getThemePalette().edgeDefault,
            lineWidth: 2,
            endArrow: {
              path: G6.Arrow.triangle(10, 10, 0),
              fill: this.getThemePalette().edgeDefault
            },
            cursor: 'pointer'
          },
          labelCfg: {
            autoRotate: true,
            style: {
              fill: this.getThemePalette().edgeLabel,
              fontSize: 12
            }
          }
        },
        nodeStateStyles: {
          hover: {
            fill: this.getThemePalette().hoverStroke,
            stroke: this.getThemePalette().hoverStroke,
            lineWidth: 3
          },
          selected: {
            fill: this.getThemePalette().selectedStroke,
            stroke: this.getThemePalette().selectedStroke,
            lineWidth: 3
          },
          'edit-selected': {
            stroke: this.getThemePalette().editSelectedStroke,
            lineWidth: 4,
            lineDash: [6, 3],
            shadowBlur: 12,
            shadowColor: this.getThemePalette().editSelectedStroke
          },
          'edit-pending': {
            stroke: this.getThemePalette().editPendingStroke,
            lineWidth: 3,
            lineDash: [5, 5]
          }
        },
        edgeStateStyles: {
          hover: {
            stroke: '#40a9ff',
            lineWidth: 3
          },
          selected: {
            stroke: '#ff4d4f',
            lineWidth: 3
          },
          inactive: {
            opacity: 0.2
          },
          'edit-selected': {
            stroke: this.getThemePalette().edgeEditSelectedStroke,
            lineWidth: 4
          }
        }
      },
      { container, width, height }
    )

    if (!this.graph) return

    this.graph.data({
      nodes: processedNodes,
      edges: processedEdges
    })
    this.graph.render()

    this.setupEvents()

    // 初始渲染后适配画布：参考 overview.html 的做法，分多次、带 padding 调整视图
    const scheduleFitView = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.fitView(20)
          })
        })
      })
    }

    // 布局开始后先适配一次
    scheduleFitView()
    // 布局逐渐收敛，再适配一到两次，确保整图都在视口内
    window.setTimeout(scheduleFitView, 300)
    window.setTimeout(scheduleFitView, 1500)
  }

  public update(nodes: GraphNode[], edges: GraphEdge[]) {
    if (!this.graph) return

    const { processedNodes, processedEdges } = this.processData(nodes, edges)

    this.graph.data({
      nodes: processedNodes,
      edges: processedEdges
    })
    this.graph.render()

    const scheduleFitView = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.fitView(20)
          })
        })
      })
    }

    scheduleFitView()
    window.setTimeout(scheduleFitView, 300)
    window.setTimeout(scheduleFitView, 1500)
  }

  public resetLayout() {
    if (!this.graph) return

    const currentData = {
      nodes: this.graph.getNodes().map((node: INode) => {
        const model = node.getModel() as any
        delete model.x
        delete model.y
        return model
      }),
      edges: this.graph.getEdges().map((edge: IEdge) => edge.getModel() as any)
    }

    this.graph.data(currentData)
    this.graph.render()

    const scheduleFitView = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.fitView(20)
          })
        })
      })
    }

    scheduleFitView()
    window.setTimeout(scheduleFitView, 300)
    window.setTimeout(scheduleFitView, 1500)
  }

  /** 切换布局类型：力导向（更分散）或环形（中心放射，适合枢纽图） */
  public switchLayout(type: LayoutType) {
    if (!this.graph || !this.container) return
    this.currentLayoutType = type
    const w = this.container.offsetWidth
    const h = this.container.offsetHeight
    const newLayout = this.getLayoutConfig(type, this.container, w, h)
    ;(this.graph as any).updateLayout(newLayout)
    const scheduleFitView = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.fitView(20)
          })
        })
      })
    }
    window.setTimeout(scheduleFitView, 400)
    window.setTimeout(scheduleFitView, 1200)
  }

  /** 当前布局类型 */
  public getLayoutType(): LayoutType {
    return this.currentLayoutType
  }

  /**
   * G6 圆形节点直径（像素）。接口常把 `size` 当权重/等级给 1～10，若直接当直径会极小；
   * 低度数时原公式最小为 15，编辑模式下点选困难，故设下限并仅在大于等于下限时采信 `node.size`。
   */
  private resolveNodeVisualSize(node: GraphNode, degree: number): number {
    const auto = Math.min(15 + degree * 3, 40)
    const floor = 28
    const max = 72
    const base = Math.max(auto, floor)
    const raw = node.size
    if (typeof raw === 'number' && Number.isFinite(raw) && raw >= floor) {
      return Math.min(Math.max(raw, floor), max)
    }
    return base
  }

  private processData(nodes: GraphNode[], edges: GraphEdge[]) {
    // 计算节点度数
    const nodeDegree: Record<string, number> = {}
    edges.forEach(edge => {
      nodeDegree[edge.source] = (nodeDegree[edge.source] || 0) + 1
      nodeDegree[edge.target] = (nodeDegree[edge.target] || 0) + 1
    })

    const processedNodes = nodes.map(node => {
      const degree = nodeDegree[node.id] || 0
      return {
        ...node,
        size: this.resolveNodeVisualSize(node, degree),
        style: {
          fill: node.style?.fill || this.getColorByType(node.type),
          stroke: node.style?.stroke || this.getThemePalette().nodeStroke,
          lineWidth: node.style?.lineWidth || 2
        },
        labelCfg: {
          ...(node as any).labelCfg,
          style: {
            ...((node as any).labelCfg?.style || {}),
            fill: this.getThemePalette().nodeLabel,
            fontSize: 16,
            fontWeight: 'bold'
          }
        }
      }
    })

    const processedEdges = edges.map((edge, index) => {
      const relationColor = this.getColorByRelation(edge.relation_type)
      return {
        ...edge,
        id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
        style: {
          stroke: edge.style?.stroke || relationColor,
          lineWidth: edge.style?.lineWidth || 2
        },
        label: edge.label,
        labelCfg: {
          style: {
            fill: relationColor,
            fontSize: 12
          }
        }
      }
    })

    return { processedNodes, processedEdges }
  }

  // 基础的高亮交互（悬停/选中相关节点和边）
  private setupEvents() {
    if (!this.graph) return

    const graph = this.graph

    // 获取节点文字对应的 shape（兼容不同 G6 版本）
    const getNodeLabelShape = (node: INode): any | null => {
      const anyNode: any = node as any

      try {
        if (anyNode.getLabel) {
          const lbl = anyNode.getLabel()
          if (lbl && typeof lbl.attr === 'function') return lbl
        }
      } catch {
        // ignore
      }

      let group: any = null
      try {
        if (anyNode.getContainer) {
          group = anyNode.getContainer()
        } else if (anyNode.get) {
          group = anyNode.get('group')
        }
      } catch {
        // ignore
      }

      if (!group) return null

      let children: any[] = []
      try {
        if (group.getChildren) {
          children = group.getChildren()
        } else if (group.get) {
          children = group.get('children') || []
        }
      } catch {
        // ignore
      }

      for (let i = 0; i < children.length; i++) {
        const child = children[i]
        if (!child || typeof child.get !== 'function') continue
        const name = child.get('name') || ''
        const type = child.get('type') || ''
        if ((name && String(name).includes('text')) || type === 'text') {
          return child
        }
      }
      return null
    }

    const highlightRelated = (node: INode) => {
      try {
        const nodeId = (node.getModel() as any).id
        const activeNodeIds = new Set<string>([nodeId])
        const edges = node.getEdges()

        edges.forEach(edge => {
          graph.setItemState(edge, 'hover', true)
          const edgeModel = edge.getModel() as any
          const relatedNodeId = edgeModel.source === nodeId ? edgeModel.target : edgeModel.source
          const relatedNode = graph.findById(relatedNodeId)
          if (relatedNode) {
            activeNodeIds.add(relatedNodeId)
            graph.setItemState(relatedNode, 'hover', true)
          }
        })

        // 非关联节点文字变浅（通过 inactive 状态 + 直接修改 label 透明度）
        graph.getNodes().forEach(n => {
          const id = (n.getModel() as any).id
          const labelShape = getNodeLabelShape(n)
          if (!activeNodeIds.has(id)) {
            graph.setItemState(n, 'inactive', true)
            if (labelShape) {
              labelShape.attr({
                opacity: 0.35
              })
            }
          } else if (labelShape) {
            // 相关节点文字保持正常
            labelShape.attr({
              opacity: 1
            })
          }
        })

        graph.getEdges().forEach(edge => {
          const edgeModel = edge.getModel() as any
          if (edgeModel.source !== nodeId && edgeModel.target !== nodeId) {
            graph.setItemState(edge, 'inactive', true)
          }
        })
      } catch {
        // ignore
      }
    }

    const unhighlightAll = () => {
      try {
        graph.getNodes().forEach(node => {
          graph.clearItemStates(node, ['hover', 'inactive', 'selected'])
          const labelShape = getNodeLabelShape(node)
          if (labelShape) {
            labelShape.attr({
              opacity: 1
            })
          }
        })
        graph.getEdges().forEach(edge => {
          graph.clearItemStates(edge, ['hover', 'inactive', 'selected'])
        })
      } catch {
        // ignore
      }
    }

    graph.on('node:mouseenter', e => {
      const node = e.item as INode
      graph.setItemState(node, 'hover', true)
      highlightRelated(node)
    })

    graph.on('node:mouseleave', e => {
      const node = e.item as INode
      graph.setItemState(node, 'hover', false)
      unhighlightAll()
    })

    graph.on('node:click', e => {
      const node = e.item as INode
      const selected = (node as any).hasState?.('selected')

      graph.getNodes().forEach(n => graph.clearItemStates(n, ['selected']))
      graph.getEdges().forEach(ed => graph.clearItemStates(ed, ['selected']))

      if (!selected) {
        graph.setItemState(node, 'selected', true)
        highlightRelated(node)
      } else {
        unhighlightAll()
      }
    })

    graph.on('canvas:click', () => {
      unhighlightAll()
    })

    // ===== 左键拖动节点时的"拖网"效果（与布局参数无关，始终生效） =====
    const dragContext: {
      lastX: number
      lastY: number
      neighborIds: Set<string>
    } = {
      lastX: 0,
      lastY: 0,
      neighborIds: new Set<string>()
    }

    graph.on('node:dragstart', e => {
      const node = e.item as INode
      const model = node.getModel() as any
      dragContext.lastX = model.x ?? 0
      dragContext.lastY = model.y ?? 0
      dragContext.neighborIds.clear()

      const nodeId = model.id
      const edges = node.getEdges()
      edges.forEach(edge => {
        const m = edge.getModel() as any
        const relatedId = m.source === nodeId ? m.target : m.source
        if (relatedId) {
          dragContext.neighborIds.add(relatedId)
        }
      })
    })

    graph.on('node:drag', e => {
      const node = e.item as INode
      const model = node.getModel() as any
      const x = model.x ?? 0
      const y = model.y ?? 0

      const dx = x - dragContext.lastX
      const dy = y - dragContext.lastY

      dragContext.lastX = x
      dragContext.lastY = y

      if (!dx && !dy) return

      // 只轻微带动一部分位移，形成阻尼感，避免整张图一起平移
      const factor = 0.3

      dragContext.neighborIds.forEach(id => {
        const related = graph.findById(id)
        if (!related) return
        const m = related.getModel() as any
        const rx = (m.x ?? 0) + dx * factor
        const ry = (m.y ?? 0) + dy * factor
        graph.updateItem(related, { x: rx, y: ry } as any)
      })
    })

    graph.on('node:dragend', () => {
      dragContext.neighborIds.clear()
    })

    // ===== 右键拖动画布功能（与 overview.html 行为一致的简化版） =====
    if (this.container) {
      // 屏蔽浏览器默认右键菜单
      this.contextMenuHandler = (e: MouseEvent) => {
        e.preventDefault()
      }
      this.container.addEventListener('contextmenu', this.contextMenuHandler)
    }

    let isRightMouseDown = false
    let lastPoint = { x: 0, y: 0 }

    const endRightDrag = () => {
      if (!isRightMouseDown) return
      isRightMouseDown = false
      if (this.container) {
        this.container.style.cursor = 'default'
      }
    }

    graph.on('canvas:mousedown', (e: any) => {
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (evt && evt.button === 2) {
        isRightMouseDown = true
        lastPoint = { x: evt.clientX, y: evt.clientY }
        if (this.container) {
          this.container.style.cursor = 'grabbing'
        }
        evt.preventDefault()
      }
    })

    graph.on('canvas:mousemove', (e: any) => {
      if (!isRightMouseDown) return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt) return

      const currentX = evt.clientX
      const currentY = evt.clientY
      const dx = currentX - lastPoint.x
      const dy = currentY - lastPoint.y

      const group = graph.get('group')
      if (group) {
        const matrix = group.getMatrix() || [1, 0, 0, 0, 1, 0, 0, 0, 1]
        matrix[6] += dx
        matrix[7] += dy
        group.setMatrix(matrix)
      }

      lastPoint = { x: currentX, y: currentY }
      evt.preventDefault()
    })

    graph.on('canvas:mouseup', () => {
      endRightDrag()
    })

    graph.on('canvas:mouseleave', () => {
      endRightDrag()
    })

    this.globalMouseUpHandler = (e: MouseEvent) => {
      if (e.button === 2) {
        endRightDrag()
      }
    }
    document.addEventListener('mouseup', this.globalMouseUpHandler)
  }

  // ========== 编辑模式事件 ==========
  private setupEditModeEvents() {
    if (!this.graph) return

    const graph = this.graph

    // 点击画布空白处：根据工具类型决定行为
    graph.on('canvas:click', (e: any) => {
      if (!this.isEditMode) return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt || evt.clientX == null) return
      const point = graph.getPointByClient(evt.clientX, evt.clientY)

      // 检查是否点击在节点上
      const nodes = graph.getNodes()
      let clickedOnNode = false
      for (const node of nodes) {
        const model = node.getModel() as any
        const dx = (model.x ?? 0) - point.x
        const dy = (model.y ?? 0) - point.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const nodeRadius = (model.size ?? 20) + 10
        if (distance < nodeRadius) {
          clickedOnNode = true
          break
        }
      }

      if (!clickedOnNode) {
        if (this.toolType === 'addNode') {
          this.editCallbacks.onAddNode?.(point.x, point.y)
        } else if (this.toolType === 'addEdge' && this.edgeSourceNodeId) {
          this.resetEdgeSelectState()
        }
      }
    })

    // Alt + 左键按下：从节点拖出预览线
    graph.on('node:mousedown', (e: any) => {
      if (!this.isEditMode) return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt || evt.button !== 0 || !evt.altKey) return
      const node = e.item as INode
      const model = node.getModel() as any
      evt.preventDefault()
      evt.stopPropagation()
      this.beginAltLinkDrag(model.id as string)
    })

    // 点击节点：根据工具类型分发行为
    graph.on('node:click', (e: any) => {
      if (!this.isEditMode) return
      if (this.suppressNextNodeClick) {
        this.suppressNextNodeClick = false
        return
      }

      const node = e.item as INode
      const model = node.getModel() as any
      const nodeId = model.id
      const x = model.x ?? 0
      const y = model.y ?? 0

      // deleteNode：点击即删
      if (this.toolType === 'deleteNode') {
        this.editCallbacks.onDeleteNode?.(nodeId)
        return
      }

      // addEdge：链式选择两个节点连线
      if (this.toolType === 'addEdge') {
        this.handleEdgeToolClick(nodeId, x, y)
        return
      }

      // deleteEdge / select / addNode：走原有的 pendingEdge 两次点击连线流程
      if (this.toolType === 'deleteEdge') return

      if (this.pendingEdge) {
        if (this.pendingEdge.sourceId === nodeId) {
          graph.clearItemStates(node, ['edit-pending'])
          this.clearPendingEdgeOnly()
          return
        }
        const sourceId = this.pendingEdge.sourceId
        const sourceNode = graph.findById(sourceId)
        if (sourceNode) graph.clearItemStates(sourceNode, ['edit-pending'])
        this.clearPendingEdgeOnly()
        this.fireProposeConnection(sourceId, nodeId)
      } else {
        graph.setItemState(node, 'edit-pending', true)
        this.pendingEdge = { sourceId: nodeId, x, y }
        this.attachPendingPreviewListeners()
      }
    })

    // addNode 工具模式下点击节点：触发 onNodeClick 回调供外部使用（可改名等）
    graph.on('node:mousedown', (e: any) => {
      if (!this.isEditMode || this.toolType !== 'addNode') return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt || evt.button !== 0) return
      const node = e.item as INode
      const model = node.getModel() as any
      evt.preventDefault()
    })

    // addNode 工具模式下点击节点（mouseup）：通知回调
    graph.on('node:mouseup', (e: any) => {
      if (!this.isEditMode || this.toolType !== 'addNode') return
      const node = e.item as INode
      const model = node.getModel() as any
      this.editToolCallbacks.onNodeClick?.(model.id)
    })

    // deleteEdge 工具模式下点击边：直接删除
    graph.on('edge:click', (e: any) => {
      if (!this.isEditMode) return
      const edge = e.item as IEdge
      const model = edge.getModel() as any
      const edgeId = model.id

      if (this.toolType === 'deleteEdge') {
        this.editCallbacks.onDeleteEdge?.(edgeId)
        return
      }

      // select 模式：选中边
      graph.getEdges().forEach(ed => graph.clearItemStates(ed, ['edit-selected']))
      graph.getNodes().forEach(n => graph.clearItemStates(n, ['edit-selected']))
      graph.setItemState(edge, 'edit-selected', true)
    })

    // 双击节点
    graph.on('node:dblclick', (e: any) => {
      if (!this.isEditMode) return
      const node = e.item as INode
      const model = node.getModel() as any
      console.log('[EditMode] Double-click node to edit:', model.id, model.label)
    })

    // 双击边
    graph.on('edge:dblclick', (e: any) => {
      if (!this.isEditMode) return
      const edge = e.item as IEdge
      const model = edge.getModel() as any
      console.log('[EditMode] Double-click edge to edit:', model.id)
    })

    // 键盘事件
    this.globalKeyDownHandler = (e: KeyboardEvent) => {
      if (!this.isEditMode) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedNodes = graph.getNodes().filter(n => n.hasState('selected'))
        const selectedEdges = graph.getEdges().filter(ed => ed.hasState('selected'))

        selectedNodes.forEach(node => {
          const model = node.getModel() as any
          this.editCallbacks.onDeleteNode?.(model.id)
        })
        selectedEdges.forEach(edge => {
          const model = edge.getModel() as any
          this.editCallbacks.onDeleteEdge?.(model.id)
        })

        graph.getNodes().forEach(n => graph.clearItemStates(n, ['selected']))
        graph.getEdges().forEach(ed => graph.clearItemStates(ed, ['selected']))
      }

      if (e.key === 'Escape') {
        this.clearPendingEdgeOnly()
        this.resetEdgeSelectState()
      }
    }
    document.addEventListener('keydown', this.globalKeyDownHandler)

    // 节点右键菜单
    graph.on('node:contextmenu', (e: any) => {
      if (!this.isEditMode) return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt) return
      evt.preventDefault()
      evt.stopPropagation()

      const node = e.item as INode
      const model = node.getModel() as any
      const menuX = evt.offsetX ?? e.x ?? 0
      const menuY = evt.offsetY ?? e.y ?? 0

      this.editCallbacks.onNodeContextMenu?.(
        model.id,
        model.label ?? '',
        (model.style?.fill as string) ?? '',
        menuX,
        menuY,
        evt.clientX,
        evt.clientY
      )
    })

    // 边右键菜单
    graph.on('edge:contextmenu', (e: any) => {
      if (!this.isEditMode) return
      const evt: MouseEvent | undefined = (e as any).originalEvent || e
      if (!evt) return
      evt.preventDefault()
      evt.stopPropagation()

      const edge = e.item as IEdge
      const model = edge.getModel() as any
      const menuX = evt.offsetX ?? e.x ?? 0
      const menuY = evt.offsetY ?? e.y ?? 0

      this.editCallbacks.onEdgeContextMenu?.(
        model.id,
        model.source ?? '',
        model.target ?? '',
        model.label ?? '',
        menuX,
        menuY,
        evt.clientX,
        evt.clientY
      )
    })
  }

  /** addEdge 工具的节点点击处理：链式选择两个节点后触发连线 */
  private handleEdgeToolClick(nodeId: string, x: number, y: number) {
    if (!this.graph) return
    const graph = this.graph

    if (!this.edgeSourceNodeId) {
      // 第一个节点：标记为选中（edit-selected 状态使节点放大+加粗）
      graph.setItemState(graph.findById(nodeId)!, 'edit-selected', true)
      this.edgeSourceNodeId = nodeId
      this.edgeSourcePos = { x, y }
      this.attachPendingPreviewListeners()
    } else if (this.edgeSourceNodeId === nodeId) {
      // 点击同一节点：取消选择
      graph.clearItemStates(graph.findById(nodeId)!, ['edit-selected'])
      this.resetEdgeSelectState()
    } else {
      // 第二个节点：触发连线确认
      const sourceId = this.edgeSourceNodeId
      graph.clearItemStates(graph.findById(sourceId)!, ['edit-selected'])
      this.resetEdgeSelectState()
      this.fireProposeConnection(sourceId, nodeId)
    }
  }

  public override destroy() {
    this.clearPendingConnection()
    this.resetEdgeSelectState()
    const svg = this.linkPreviewSvg
    if (svg) {
      try {
        // 更稳妥：直接 remove，避免 parentNode 在异步阶段变为 null
        svg.remove()
      } catch {
        try {
          svg.parentNode?.removeChild?.(svg)
        } catch {
          // ignore
        }
      }
    }
    this.linkPreviewSvg = null
    this.linkPreviewLine = null
    // 清理右键相关事件
    if (this.container && this.contextMenuHandler) {
      this.container.removeEventListener('contextmenu', this.contextMenuHandler)
    }
    if (this.globalMouseUpHandler) {
      document.removeEventListener('mouseup', this.globalMouseUpHandler)
    }
    if (this.globalKeyDownHandler) {
      document.removeEventListener('keydown', this.globalKeyDownHandler)
    }
    this.contextMenuHandler = null
    this.globalMouseUpHandler = null
    this.globalKeyDownHandler = null
    this.pendingEdge = null

    super.destroy()
  }
}
